import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import { type Security, safeAbsurd } from '@ipfabric/utils';
import classNames from 'classnames';
import { renderSecurityValue } from 'components/SecuritySchema/helpers/renderSecurityValue';
import type {
  ComparisonTestNode,
  SecuritySchemaNodeComponentProps,
} from 'components/SecuritySchema/model/SecuritySchemaNode';
import { renderTestNodeMetaInfo } from 'components/SecuritySchema/nodeComponents/renderTestNodeMetaInfo';
import { useLocale } from 'containers/App/LocaleContext';
import type { FC, ReactNode } from 'react';
import type { AppTranslations } from 'translations/translations';

type SimpleTestTranslations =
  AppTranslations['securitySchema']['test']['simple'];

const renderPacketSelector = (
  msgSimpleTest: SimpleTestTranslations,
  ps: Security.PacketSelector,
): ReactNode => {
  switch (ps.header) {
    case 'icmp':
      return msgSimpleTest.packetSelector.icmp[ps.field];
    case 'icmpv6':
      return msgSimpleTest.packetSelector.icmpv6[ps.field];
    case 'interface':
      return msgSimpleTest.packetSelector.interface[ps.field];
    case 'ip':
      return msgSimpleTest.packetSelector.ip[ps.field];
    case 'ipv6':
      return msgSimpleTest.packetSelector.ipv6[ps.field];
    case 'l4':
      return msgSimpleTest.packetSelector.l4[ps.field];
    case 'l7':
      return msgSimpleTest.packetSelector.l7[ps.field];
    case 'options':
      return msgSimpleTest.packetSelector.options[ps.field];
    case 'tcp':
      return msgSimpleTest.packetSelector.tcp[ps.field];
    case 'udp':
      return msgSimpleTest.packetSelector.udp[ps.field];
    case 'processing':
      return msgSimpleTest.packetSelector.processing[ps.field];
    case 'eth':
      return msgSimpleTest.packetSelector.eth[ps.field];
    case 'vxlan':
      return msgSimpleTest.packetSelector.vxlan[ps.field];
    case 'mpls':
      return msgSimpleTest.packetSelector.mpls[ps.field];
    case 'natPreRouting':
      return msgSimpleTest.packetSelector.natPreRouting[ps.field];
    case 'routing':
      return msgSimpleTest.packetSelector.routing[ps.field];
    default:
      safeAbsurd(ps);
      return null;
  }
};

const getSimpleTestColorStripeClass = (
  header: Security.PacketSelector['header'],
): string | null => {
  switch (header) {
    case 'interface':
      return 'security-simple-test--l1';
    case 'ip':
    case 'ipv6':
      return 'security-simple-test--l3';
    case 'icmp':
    case 'icmpv6':
      return 'security-simple-test--icmp';
    case 'l4':
    case 'tcp':
    case 'udp':
      return 'security-simple-test--l4';
    case 'l7':
      return 'security-simple-test--l7';
    case 'options':
      return 'security-simple-test--options';
    case 'processing':
      return 'security-simple-test--processing';
    case 'vxlan':
      return 'security-simple-test--vxlan';
    case 'eth':
      return 'security-simple-test--eth';
    case 'mpls':
      return 'security-simple-test--mpls';
    case 'natPreRouting':
      return 'security-simple-test--natPreRouting';
    case 'routing':
      return 'security-simple-test--processing';
    default:
      safeAbsurd(header);
      return null;
  }
};

export const CComparisonTestNode: FC<
  SecuritySchemaNodeComponentProps<ComparisonTestNode>
> = ({ node, context }) => {
  const msg = useLocale();
  const msgTest = msg.securitySchema.test;
  return (
    <div
      className={classNames(
        'security-tree-node security-tree-node--test-leaf',
        context.highlightedSubtree && 'security-tree-node--highlighted',
      )}
    >
      {renderTestNodeMetaInfo(node, msg)}

      <div
        className={classNames(
          'security-simple-test',
          getSimpleTestColorStripeClass(node.left.header),
        )}
      >
        <TextOverflowTooltip as='p' className='security-tree-node__heading'>
          {node.operator === 'boolean==' && node.right?.value === false && (
            <span className='text-danger'>
              {`${msgTest.simple.booleanFalse} `}
            </span>
          )}
          {renderPacketSelector(msgTest.simple, node.left)}
        </TextOverflowTooltip>
        {node.operator !== 'boolean==' && (
          <>
            <TextOverflowTooltip as='p'>
              {msgTest.simple.operator[node.operator]}
            </TextOverflowTooltip>
            <TextOverflowTooltip as='p'>
              {renderSecurityValue(msgTest.simple, node.right)}
            </TextOverflowTooltip>
          </>
        )}
      </div>
    </div>
  );
};
